import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { formatPhoneNumber } from '@/components/audio-conversation/utils';
import {
  NormalizedSuccessStatus,
  SUCCESS_STATUS_DISPLAY,
} from '@/types/enhanced-bland-call';

import { formatDate, formatDuration } from '../utils';

type EvaluationSource = 'human' | 'ai' | 'unknown';

export interface CallEvaluationDisplay {
  label?: string | null;
  summary?: string | null;
  score?: number | null;
  confidence?: number | null;
  recordedAt?: string | null;
  source: EvaluationSource;
  successStatus?: NormalizedSuccessStatus | null;
  successBoolean?: boolean | null;
}

export interface CallDisplayEntry {
  id: string;
  type: 'voice_ai' | 'bpo';
  sourceCallId?: string;
  phoneNumber?: string | null;
  callerNumber?: string | null;
  requestedAt?: string | null;
  completedAt?: string | null;
  durationSeconds?: number | null;
  statusLabel?: string | null;
  evaluation?: CallEvaluationDisplay;
  detailRows?: Array<{ label: string; value: string }>;
  notes?: string | null;
}

const formatTimestamp = (value?: string | null): string => {
  if (!value) return '—';
  const result = formatDate(value);
  return result === 'Unknown' ? '—' : result;
};

const sourceBadgeClass: Record<EvaluationSource, string> = {
  human: 'bg-green-50 text-green-700 border-green-200',
  ai: 'bg-blue-50 text-blue-700 border-blue-200',
  unknown: 'bg-gray-50 text-gray-600 border-gray-200',
};

const getSuccessChip = (
  entry: CallDisplayEntry,
): { label: string; className: string } | null => {
  if (entry.type === 'voice_ai' && entry.evaluation?.successStatus) {
    const display = SUCCESS_STATUS_DISPLAY[entry.evaluation.successStatus];
    if (display) {
      return { label: display.label, className: display.className };
    }
    return null;
  }

  if (entry.type === 'bpo' && entry.evaluation?.successBoolean !== null) {
    if (entry.evaluation?.successBoolean === true) {
      return { label: 'Success', className: 'text-green-700' };
    }
    if (entry.evaluation?.successBoolean === false) {
      return { label: 'Failed', className: 'text-red-700' };
    }
  }

  return null;
};

interface CallDetailCardProps {
  entry: CallDisplayEntry;
  callNumber: number;
}

export const CallDetailCard: React.FC<CallDetailCardProps> = ({
  entry,
  callNumber,
}) => {
  const formattedPhone = entry.phoneNumber
    ? formatPhoneNumber(entry.phoneNumber)
    : 'Unknown number';
  const successChip = getSuccessChip(entry);
  const [showEvaluationDetails, setShowEvaluationDetails] = useState(false);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-gray-500">
            Call #{callNumber} •{' '}
            {entry.type === 'voice_ai' ? 'Voice AI' : 'BPO'}
          </div>
          <div className="text-base font-semibold text-gray-900">
            {formattedPhone}
          </div>
          {entry.statusLabel && (
            <div className="text-xs text-gray-500">{entry.statusLabel}</div>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          {entry.evaluation && (
            <span
              className={`px-2 py-0.5 text-xs rounded-full border ${sourceBadgeClass[entry.evaluation.source]}`}
            >
              {entry.evaluation.source === 'human'
                ? 'Human evaluation'
                : entry.evaluation.source === 'ai'
                  ? 'AI evaluation'
                  : 'Evaluation'}
            </span>
          )}
          {entry.durationSeconds ? (
            <span className="text-xs text-gray-500">
              Duration {formatDuration(entry.durationSeconds)}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div>
          <div className="text-xs uppercase text-gray-500 tracking-wide">
            Time requested
          </div>
          <div className="font-medium text-gray-900">
            {formatTimestamp(entry.requestedAt)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-gray-500 tracking-wide">
            Time completed
          </div>
          <div className="font-medium text-gray-900">
            {formatTimestamp(entry.completedAt)}
          </div>
        </div>
        <div>
          <div className="text-xs uppercase text-gray-500 tracking-wide">
            Evaluation received
          </div>
          <div className="font-medium text-gray-900">
            {entry.evaluation
              ? formatTimestamp(entry.evaluation.recordedAt)
              : '—'}
          </div>
        </div>
      </div>

      {entry.evaluation ? (
        <div className="rounded-lg border border-gray-100 bg-gray-50">
          <button
            type="button"
            onClick={() => setShowEvaluationDetails((prev) => !prev)}
            className="w-full flex items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-900"
          >
            <div className="flex items-center gap-3">
              <span>{entry.evaluation.label || 'Evaluation'}</span>
              {successChip && (
                <span
                  className={`text-xs font-semibold ${successChip.className}`}
                >
                  {successChip.label}
                </span>
              )}
            </div>
            <ChevronDownIcon
              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                showEvaluationDetails ? 'rotate-180' : ''
              }`}
            />
          </button>
          {showEvaluationDetails && (
            <div className="border-t border-gray-100 px-3 py-3 space-y-2">
              {entry.evaluation.summary && (
                <p className="text-sm text-gray-700">
                  {entry.evaluation.summary}
                </p>
              )}
              {(entry.evaluation.score != null ||
                entry.evaluation.confidence != null) && (
                <div className="text-xs text-gray-500">
                  {entry.evaluation.score != null && (
                    <span>Score {entry.evaluation.score.toFixed(2)}</span>
                  )}
                  {entry.evaluation.score != null &&
                    entry.evaluation.confidence != null && <span> • </span>}
                  {entry.evaluation.confidence != null && (
                    <span>
                      Confidence {entry.evaluation.confidence.toFixed(2)}
                    </span>
                  )}
                </div>
              )}
              {entry.detailRows && entry.detailRows.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
                  {entry.detailRows.map((row) => (
                    <div
                      key={`${entry.id}-${row.label}`}
                      className="flex gap-2"
                    >
                      <span className="text-gray-500 capitalize min-w-[110px]">
                        {row.label}:
                      </span>
                      <span className="text-gray-900 break-words">
                        {row.value}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {entry.notes && (
                <div className="text-sm text-gray-600">{entry.notes}</div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 p-3 text-sm text-gray-500">
          No evaluation recorded yet.
        </div>
      )}
    </div>
  );
};
